'use client';

// Menu déroulant — le popover en forme de LISTE.
//
// Il ne porte plus la mécanique : celle-ci vit dans `usePopover`, que consomment aussi les
// panneaux qui n'ont rien d'une liste (les filtres de la bibliothèque). Ce fichier ne décrit
// donc que le rendu d'une liste d'entrées.
//
// RAYONS : le panneau porte `overflow-hidden` et AUCUN remplissage, si bien que la première
// et la dernière entrée sont découpées par ses coins — leur survol épouse le contour au lieu
// de laisser un liseré. Le rayon est celui de la carte : deux valeurs voisines mais
// différentes se lisent comme une erreur.

import { createContext, useContext } from 'react';
import { type LucideIcon } from 'lucide-react';

import { usePopover } from '@/components/ui/usePopover';
import { fontStyle } from '@/theme/typography';

const CloseContext = createContext<() => void>(() => {});

interface MenuProps {
  /** Contenu du bouton déclencheur. */
  trigger: React.ReactNode;
  /** Libellé accessible du déclencheur. */
  label: string;
  /** Classe du déclencheur, pour qu'il s'intègre à son contexte. */
  triggerClassName?: string;
  /** Largeur du panneau (classe Tailwind). */
  width?: string;
  /**
   * Rôle du panneau. `menu` pour une liste d'actions ; `group` quand il ne contient que du
   * contenu à lire — annoncer un menu sans entrée de menu tromperait les lecteurs d'écran.
   */
  role?: 'menu' | 'group';
  children: React.ReactNode;
}

export function Menu({
  trigger,
  label,
  triggerClassName = '',
  width = 'w-60',
  role = 'menu',
  children,
}: MenuProps) {
  // Déstructuré : lire `popover.hostRef` pendant le rendu serait vu comme un accès de ref
  // par le compilateur React, alors qu'on ne fait que transmettre la référence.
  const { open, hostRef, triggerRef, toggle, dismiss } = usePopover();

  return (
    <div ref={hostRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-haspopup={role === 'menu' ? 'menu' : 'dialog'}
        aria-expanded={open}
        aria-label={label}
        className={triggerClassName}
      >
        {trigger}
      </button>

      {open ? (
        <div
          role={role}
          aria-label={role === 'group' ? label : undefined}
          onClick={(e) => e.stopPropagation()}
          className={`absolute right-0 z-30 mt-1 ${width} overflow-hidden rounded-lg border border-border bg-surface-elevated shadow-[var(--rmbr-shadow-xl)]`}
        >
          <CloseContext.Provider value={dismiss}>
            {children}
          </CloseContext.Provider>
        </div>
      ) : null}
    </div>
  );
}

interface ItemProps {
  icon?: LucideIcon;
  label: string;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Présent = l'entrée bascule un état (case à cocher), annoncé aux lecteurs d'écran. */
  checked?: boolean;
  /** Présent = l'entrée est un CHOIX EXCLUSIF parmi ses sœurs (bouton radio). */
  selected?: boolean;
  /** Décoration de fin de ligne (une coche, par exemple). */
  trailing?: React.ReactNode;
  /** Décoration de tête, quand une icône Lucide ne convient pas (un drapeau, ici). */
  leading?: React.ReactNode;
}

export function MenuItem({
  icon: Icon,
  label,
  onSelect,
  danger = false,
  disabled = false,
  checked,
  selected,
  trailing,
  leading,
}: ItemProps) {
  const close = useContext(CloseContext);

  // Trois rôles distincts : une action, une bascule, un choix exclusif. Les confondre ferait
  // annoncer « case à cocher » là où l'on choisit un tri parmi neuf.
  const role =
    selected !== undefined
      ? 'menuitemradio'
      : checked !== undefined
        ? 'menuitemcheckbox'
        : 'menuitem';

  const active = selected ?? checked;

  return (
    <button
      type="button"
      role={role}
      aria-checked={active}
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        close();
        onSelect();
      }}
      style={fontStyle('bodySm')}
      // Aucun rayon propre : c'est le panneau qui découpe, d'où des coins parfaitement
      // concentriques sur la première et la dernière entrée.
      className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors disabled:opacity-50 ${
        danger
          ? 'text-danger hover:bg-danger/10'
          : selected
            ? 'bg-action-muted text-action'
            : 'text-text-secondary hover:bg-fill-subtle hover:text-text-primary'
      }`}
    >
      {leading}
      {Icon ? <Icon className="size-4 shrink-0" /> : null}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {trailing}
    </button>
  );
}

export function MenuSeparator() {
  return <span role="none" className="block h-px bg-border" />;
}
